/**
 * Intake Analytics Component
 * 
 * Displays analytics charts for intake completion funnel,
 * trends, and score distributions
 */

import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Clock,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  getIntakeAnalytics,
  AnalyticsData,
  getTherapyCategoryLabel
} from '@/services/intakeService';
import { DatePicker } from '@/components/ui/date-picker';
import { getDateRange, DATE_RANGE_OPTIONS, type DateRangePreset } from '@/lib/dateRanges';

// ============================================================================
// TYPES
// ============================================================================

interface IntakeAnalyticsProps {
  className?: string;
  clinicId?: string;
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const FunnelChart: React.FC<{ funnel: AnalyticsData['funnel'] }> = ({ funnel }) => {
  const stages = [
    { label: 'Sent', value: funnel.sent, color: 'bg-blue-500' },
    { label: 'Opened', value: funnel.opened, color: 'bg-indigo-500' },
    { label: 'Started', value: funnel.started, color: 'bg-violet-500' },
    { label: 'Completed', value: funnel.completed, color: 'bg-brand-teal' },
  ];

  const maxValue = Math.max(...stages.map(s => s.value), 1);

  return (
    <div className="space-y-3">
      {stages.map((stage, index) => {
        const percentage = funnel.sent > 0 ? Math.round((stage.value / funnel.sent) * 100) : 0;
        const width = (stage.value / maxValue) * 100;
        const dropoff = index > 0 ? stages[index - 1].value - stage.value : 0;
        
        return (
          <div key={stage.label}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-slate-700">{stage.label}</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-900">{stage.value}</span>
                {index > 0 && dropoff > 0 && (
                  <span className="text-xs text-red-600">-{dropoff}</span>
                )}
                <span className="text-xs text-slate-500">({percentage}%)</span>
              </div>
            </div>
            <div className="h-8 bg-slate-100 rounded-lg overflow-hidden">
              <div 
                className={`h-full ${stage.color} transition-all duration-500`}
                style={{ width: `${width}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TrendChart: React.FC<{ trends: AnalyticsData['trends'] }> = ({ trends }) => {
  if (trends.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-slate-400">
        No trend data available
      </div>
    );
  }

  const maxSent = Math.max(...trends.map(t => t.sent), 1);

  return (
    <div className="flex items-end gap-2 h-40">
      {trends.map((week, index) => {
        const height = (week.sent / maxSent) * 100;
        const completedHeight = week.sent > 0 ? (week.completed / week.sent) * height : 0;
        
        return (
          <div 
            key={week.week} 
            className="flex-1 flex flex-col items-center gap-1"
          >
            <div className="relative w-full h-32 flex items-end justify-center">
              {/* Sent bar (background) */}
              <div 
                className="w-full bg-slate-200 rounded-t transition-all duration-300 relative"
                style={{ height: `${height}%` }}
              >
                {/* Completed overlay */}
                <div 
                  className="absolute bottom-0 left-0 right-0 bg-brand-teal rounded-t transition-all duration-300"
                  style={{ height: `${completedHeight}%` }}
                />
              </div>
            </div>
            <span className="text-[10px] text-slate-500">
              {new Date(week.week).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const TimeDistributionChart: React.FC<{ distribution: AnalyticsData['timeDistribution'] }> = ({ distribution }) => {
  const segments = [
    { label: '<5 min', value: distribution.under5min, color: 'bg-emerald-500' },
    { label: '5-10 min', value: distribution['5to10min'], color: 'bg-brand-teal' },
    { label: '10-15 min', value: distribution['10to15min'], color: 'bg-blue-500' },
    { label: '15-30 min', value: distribution['15to30min'], color: 'bg-amber-500' },
    { label: '>30 min', value: distribution.over30min, color: 'bg-red-500' },
  ];

  const total = segments.reduce((sum, s) => sum + s.value, 0);

  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-20 text-slate-400">
        No completion time data
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stacked bar */}
      <div className="h-8 rounded-lg overflow-hidden flex bg-slate-100">
        {segments.map((segment, index) => {
          const width = (segment.value / total) * 100;
          if (width === 0) return null;
          return (
            <div 
              key={segment.label}
              className={`${segment.color} transition-all duration-300`}
              style={{ width: `${width}%` }}
              title={`${segment.label}: ${segment.value}`}
            />
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="flex flex-wrap gap-4">
        {segments.map(segment => (
          <div key={segment.label} className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded ${segment.color}`} />
            <span className="text-xs text-slate-600">{segment.label}</span>
            <span className="text-xs text-slate-400">({segment.value})</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ScoreDistributionChart: React.FC<{ 
  title: string;
  data: AnalyticsData['scoreDistributions']['adam'] | AnalyticsData['scoreDistributions']['stopBang'];
  type: 'adam' | 'stopBang';
}> = ({ title, data, type }) => {
  const maxCount = Math.max(...Object.values(data.distribution), 1);
  const total = Object.values(data.distribution).reduce((a, b) => a + b, 0);

  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-20 text-slate-400">
        No score data
      </div>
    );
  }

  const getBarColor = (score: number): string => {
    if (type === 'adam') {
      return score >= 4 ? 'bg-amber-500' : 'bg-emerald-500';
    } else {
      if (score >= 5) return 'bg-red-500';
      if (score >= 3) return 'bg-amber-500';
      return 'bg-emerald-500';
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-900">{title}</span>
        {data.average !== null && (
          <span className="text-xs text-slate-500">Avg: {data.average}</span>
        )}
      </div>
      
      {/* Bar chart */}
      <div className="flex items-end gap-1 h-20">
        {Object.entries(data.distribution).map(([score, count]) => {
          const height = (count / maxCount) * 100;
          return (
            <div 
              key={score}
              className="flex-1 flex flex-col items-center gap-1"
            >
              <div 
                className={`w-full ${getBarColor(parseInt(score))} rounded-t transition-all duration-300`}
                style={{ height: `${Math.max(height, count > 0 ? 5 : 0)}%` }}
                title={`Score ${score}: ${count} patients`}
              />
              <span className="text-[10px] text-slate-500">{score}</span>
            </div>
          );
        })}
      </div>
      
      {/* Summary badges */}
      <div className="flex flex-wrap gap-2 mt-2">
        {type === 'adam' ? (
          <>
            <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] px-2 py-0.5">
              Positive: {(data as AnalyticsData['scoreDistributions']['adam']).positive}
            </Badge>
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] px-2 py-0.5">
              Negative: {(data as AnalyticsData['scoreDistributions']['adam']).negative}
            </Badge>
          </>
        ) : (
          <>
            <Badge className="bg-red-50 text-red-700 border-red-200 text-[10px] px-2 py-0.5">
              High: {(data as AnalyticsData['scoreDistributions']['stopBang']).high}
            </Badge>
            <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] px-2 py-0.5">
              Intermediate: {(data as AnalyticsData['scoreDistributions']['stopBang']).intermediate}
            </Badge>
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] px-2 py-0.5">
              Low: {(data as AnalyticsData['scoreDistributions']['stopBang']).low}
            </Badge>
          </>
        )}
      </div>
    </div>
  );
};

const CategoryBreakdown: React.FC<{ byCategory: AnalyticsData['byCategory'] }> = ({ byCategory }) => {
  const categories = Object.entries(byCategory);

  if (categories.length === 0) {
    return (
      <div className="flex items-center justify-center h-20 text-slate-400">
        No category data
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {categories.map(([category, data]) => (
        <div key={category} className="p-3 rounded-lg bg-slate-50 border border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-900">
              {getTherapyCategoryLabel(category as any)}
            </span>
            <Badge className="bg-brand-teal/10 text-brand-teal border-brand-teal/20 text-[10px] px-2 py-0.5">
              {data.completionRate}% complete
            </Badge>
          </div>
          <div className="grid grid-cols-3 gap-4 text-xs">
            <div>
              <span className="text-slate-500">Total</span>
              <p className="text-slate-900 font-medium">{data.total}</p>
            </div>
            <div>
              <span className="text-slate-500">Completed</span>
              <p className="text-emerald-600 font-medium">{data.completed}</p>
            </div>
            <div>
              <span className="text-slate-500">Avg. Time</span>
              <p className="text-slate-900 font-medium">{data.avgCompletionMinutes}m</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const IntakeAnalytics: React.FC<IntakeAnalyticsProps> = ({ className, clinicId }) => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('this_month');
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>();
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>();

  useEffect(() => {
    loadAnalytics();
  }, [dateRangePreset, customStartDate, customEndDate, clinicId]);

  const loadAnalytics = async () => {
    setIsLoading(true);
    setError(null);

    try {
      let startDate: string;
      let endDate: string;
      if (dateRangePreset === 'custom' && customStartDate && customEndDate) {
        startDate = customStartDate.toISOString();
        endDate = customEndDate.toISOString();
      } else if (dateRangePreset !== 'custom') {
        const range = getDateRange(dateRangePreset);
        startDate = range.startDate;
        endDate = range.endDate;
      } else {
        const range = getDateRange('this_month');
        startDate = range.startDate;
        endDate = range.endDate;
      }

      const result = await getIntakeAnalytics(startDate, endDate, clinicId);
      
      if (result.success && result.data) {
        setAnalytics(result.data);
      } else {
        setError(result.error || 'Failed to load analytics');
      }
    } catch (err) {
      setError('An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-brand-teal" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <p className="text-slate-500">{error}</p>
        <Button 
          variant="outline" 
          onClick={loadAnalytics} 
          className="mt-4 border-slate-200 text-slate-700"
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!analytics) return null;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header with date range selector */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-brand-teal" />
          Intake Analytics
        </h3>
        <div className="flex items-center gap-2">
          <Select value={dateRangePreset} onValueChange={(v) => setDateRangePreset(v as DateRangePreset)}>
            <SelectTrigger className="w-[150px] bg-white border-slate-200 text-slate-700">
              <SelectValue placeholder="Date Range" />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              {DATE_RANGE_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {dateRangePreset === 'custom' && (
            <div className="flex items-center gap-2">
              <DatePicker
                value={customStartDate}
                onValueChange={setCustomStartDate}
                placeholder="Start date"
                maxDate={customEndDate || new Date()}
                className="w-[140px]"
                inputClassName="h-9 text-sm"
              />
              <span className="text-sm text-slate-400">to</span>
              <DatePicker
                value={customEndDate}
                onValueChange={setCustomEndDate}
                placeholder="End date"
                minDate={customStartDate}
                maxDate={new Date()}
                className="w-[140px]"
                inputClassName="h-9 text-sm"
              />
            </div>
          )}
        </div>
      </div>

      {/* Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Completion Funnel */}
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <h4 className="text-sm font-medium text-slate-600 mb-4 flex items-center gap-2">
            <Users className="w-4 h-4" />
            Completion Funnel
          </h4>
          <FunnelChart funnel={analytics.funnel} />
        </div>

        {/* Weekly Trends */}
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <h4 className="text-sm font-medium text-slate-600 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Weekly Trends
          </h4>
          <TrendChart trends={analytics.trends} />
          <div className="flex items-center justify-center gap-4 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-slate-200" />
              <span className="text-xs text-slate-500">Sent</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-brand-teal" />
              <span className="text-xs text-slate-500">Completed</span>
            </div>
          </div>
        </div>

        {/* Completion Time Distribution */}
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <h4 className="text-sm font-medium text-slate-600 mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Completion Time Distribution
          </h4>
          <TimeDistributionChart distribution={analytics.timeDistribution} />
        </div>

        {/* Category Breakdown */}
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <h4 className="text-sm font-medium text-slate-600 mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            By Category
          </h4>
          <CategoryBreakdown byCategory={analytics.byCategory} />
        </div>

        {/* ADAM Score Distribution */}
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <h4 className="text-sm font-medium text-slate-600 mb-4">
            ADAM Questionnaire Scores
          </h4>
          <ScoreDistributionChart 
            title="ADAM Score Distribution" 
            data={analytics.scoreDistributions.adam}
            type="adam"
          />
        </div>

        {/* STOP-Bang Score Distribution */}
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <h4 className="text-sm font-medium text-slate-600 mb-4">
            STOP-Bang Sleep Apnea Scores
          </h4>
          <ScoreDistributionChart 
            title="STOP-Bang Score Distribution" 
            data={analytics.scoreDistributions.stopBang}
            type="stopBang"
          />
        </div>
      </div>
    </div>
  );
};

export default IntakeAnalytics;
